import { useEffect, useRef, type MutableRefObject } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import { theme } from 'antd'
import { EnterToSend } from './extensions/EnterToSend'
import { createMentionSuggestion } from './extensions/MentionSuggestion'
import type { User } from '../../Contracts'

interface TiptapEditorProps {
  onSend: () => void
  onCancel: () => void
  onContentChange: (hasContent: boolean) => void
  // `| undefined` explicitly: exactOptionalPropertyTypes forbids passing a
  // possibly-undefined value to a plain optional prop
  initialContent?: string | undefined
  participants: User[]
  editorRef?: MutableRefObject<Editor | null>
  isDark: boolean
  primaryColor: string
}

const TiptapEditor = ({
  onSend,
  onCancel,
  onContentChange,
  initialContent,
  participants,
  editorRef,
  isDark,
  primaryColor,
}: TiptapEditorProps) => {
  const { token } = theme.useToken()

  // Refs to avoid stale closures — extensions are created once,
  // but these refs always hold the latest values
  const onSendRef = useRef(onSend)
  const onCancelRef = useRef(onCancel)
  const onContentChangeRef = useRef(onContentChange)
  const participantsRef = useRef(participants)
  const themeRef = useRef({ isDark, primaryColor })

  onSendRef.current = onSend
  onCancelRef.current = onCancel
  onContentChangeRef.current = onContentChange
  participantsRef.current = participants
  themeRef.current = { isDark, primaryColor }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: 'Написати повідомлення…',
      }),
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: createMentionSuggestion(
          () => participantsRef.current,
          () => themeRef.current,
        ),
      }),
      EnterToSend.configure({
        onSend: () => onSendRef.current(),
        onCancel: () => onCancelRef.current(),
      }),
    ],
    content: initialContent ?? '',
    onUpdate: ({ editor: e }) => {
      onContentChangeRef.current(!!e.getText().trim())
    },
    immediatelyRender: true,
  })

  // Expose editor instance to parent
  useEffect(() => {
    if (editorRef) editorRef.current = editor
    return () => {
      if (editorRef) editorRef.current = null
    }
  }, [editor, editorRef])

  // Pre-fill for edit mode
  useEffect(() => {
    if (editor && initialContent !== undefined) {
      const currentHtml = editor.getHTML()
      if (currentHtml !== initialContent) {
        editor.commands.setContent(initialContent)
        editor.commands.focus('end')
      }
    }
  }, [editor, initialContent])

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        // Вертикальний auto-margin центрує редактор у рядку вводу
        // незалежно від align-items: auto-марджини у flexbox поглинають
        // вільний cross-axis простір ДО спрацювання align-self, тож
        // перший рядок тексту завжди стоїть по центру іконок
        margin: 'auto 0',
        ['--placeholder-color' as string]: token.colorTextQuaternary,
        ['--mention-bg' as string]: token.colorPrimaryBg,
        ['--mention-color' as string]: token.colorPrimary,
        ['--code-bg' as string]: token.colorFillTertiary,
        ['--link-color' as string]: token.colorPrimary,
      }}
    >
      <EditorContent
        editor={editor}
        className="tiptap-input chat-scroll"
        style={{ color: token.colorText }}
      />
    </div>
  )
}

export default TiptapEditor
